import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Copy, Check, AlignLeft, Music2,
  Paperclip, ExternalLink, Download, Trash2, Link2, X,
} from 'lucide-react';
import DeleteButton from './ui/DeleteButton';
import { ChordProParser, HtmlDivFormatter } from 'chordsheetjs';
import { postProcessChordHtml, transposeKey, detectAccidental, stripToLyrics } from '../utils/chordpro';
import { getStoredCapo, setStoredCapo, parseCapoDirective, CAPO_MIN, CAPO_MAX } from '../utils/capo';
import AddLinkModal from './AddLinkModal';
import { servicesAPI } from '../utils/api';

/* ─── ChordSheetJS styles ─────────────────────────────────────────────── */
const CHORD_SHEET_CSS = `
  .sdp-csj-wrap .paragraph { margin-bottom: 1.5rem; }
  .sdp-csj-wrap .row { display: flex; flex-wrap: wrap; line-height: 1; }
  .sdp-csj-wrap .column { display: inline-flex; flex-direction: column; }
  .sdp-csj-wrap .chord {
    font-family: ui-monospace,SFMono-Regular,Menlo,monospace;
    font-size: 0.75rem; font-weight: 700; color: #16a34a;
    line-height: 1.3; min-height: 1.3em; white-space: pre; padding-right: 0.4em;
  }
  .sdp-csj-wrap .chord:empty { visibility: hidden; }
  .sdp-csj-wrap .lyrics {
    font-family: ui-monospace,SFMono-Regular,Menlo,monospace;
    font-size: 0.875rem; line-height: 1.45; color: #1e293b;
    white-space: pre-wrap; word-break: break-word; overflow-wrap: break-word; padding-right: 0.4em;
  }
  .sdp-csj-wrap .comment {
    display:inline-block; font-size:0.7rem; font-weight:700; text-transform:uppercase;
    letter-spacing:0.1em; color:#16a34a; background:rgba(22,163,74,0.1);
    border-radius:0.25rem; padding:0.1em 0.5em; margin-top:0.25rem; margin-bottom:0.125rem;
  }
  .sdp-csj-wrap.sdp-lyrics-mode .chord { display: none !important; min-height: 0 !important; }
  .sdp-csj-wrap.sdp-lyrics-mode .row.row--chord-only { display: none !important; }
  .sdp-csj-wrap.sdp-lyrics-mode .column { flex-direction: row; }
  .dark .sdp-csj-wrap .chord { color: #4ade80; }
  .dark .sdp-csj-wrap .lyrics { color: #f1f5f9; }
  .dark .sdp-csj-wrap .comment { color: #4ade80; background: rgba(74,222,128,0.1); }
`;

const _parser = new ChordProParser({ normalizeChords: false });
const _formatter = new HtmlDivFormatter({ normalizeChords: false });

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getDomain = (url) => {
  try { return new URL(url).hostname.replace('www.', ''); }
  catch { return url; }
};

const getYouTubeId = (url) => {
  try {
    const u = new URL(url);
    if (u.hostname === 'youtu.be') return u.pathname.slice(1).split('?')[0];
    return u.searchParams.get('v') || '';
  } catch { return ''; }
};
const isYouTube = (url) => /youtube\.com|youtu\.be/i.test(url);
const isSpotify = (url) => /spotify\.com/i.test(url);

/** Rich link row — YouTube card, Spotify badge, or favicon+label for everything else */
const RichLink = ({ link, linkIdx, hasEditPermission, onRemove }) => {
  const url = link.url || '';
  const label = link.label || '';
  const domain = getDomain(url);

  if (isYouTube(url)) {
    const ytId = getYouTubeId(url);
    return (
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-800"
        data-testid={`song-detail-link-${linkIdx}`}>
        <a href={url} target="_blank" rel="noopener noreferrer" className="block relative group">
          {ytId ? (
            <img
              src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`}
              alt={label || 'YouTube'}
              className="w-full h-36 object-cover bg-slate-200 dark:bg-slate-700"
              onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
            />
          ) : (
            <div className="w-full h-36 bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
              <ExternalLink className="w-8 h-8 text-red-400" />
            </div>
          )}
          {/* Play overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/25 transition-colors">
            <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center shadow-lg">
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white ml-0.5"><polygon points="5,3 19,12 5,21"/></svg>
            </div>
          </div>
        </a>
        <div className="flex items-center gap-2 px-3 py-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 dark:text-white truncate leading-snug">
              {label || 'YouTube Video'}
            </p>
            <span className="text-[10px] font-semibold text-red-500 uppercase tracking-wide">YouTube</span>
          </div>
          {hasEditPermission && (
            <button onClick={() => onRemove(linkIdx)}
              className="p-1.5 text-slate-400 hover:text-rose-500 transition-colors shrink-0"
              data-testid={`sdp-remove-link-${linkIdx}`}>
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Generic rich row with favicon
  const faviconUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
  const spotifyColor = isSpotify(url) ? 'text-green-600 dark:text-green-400' : 'text-blue-500 dark:text-blue-400';

  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
      data-testid={`song-detail-link-${linkIdx}`}>
      <img
        src={faviconUrl}
        alt=""
        className="w-5 h-5 rounded shrink-0"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
          {label || domain}
        </p>
        {label && <p className={`text-xs truncate ${spotifyColor}`}>{domain}</p>}
      </div>
      <a href={url} target="_blank" rel="noopener noreferrer"
        className="p-1.5 text-slate-400 hover:text-blue-500 transition-colors shrink-0"
        data-testid={`sdp-open-link-${linkIdx}`}
        onClick={(e) => e.stopPropagation()}>
        <ExternalLink className="w-4 h-4" />
      </a>
      {hasEditPermission && (
        <button onClick={() => onRemove(linkIdx)}
          className="p-1.5 text-slate-400 hover:text-rose-500 transition-colors shrink-0"
          data-testid={`sdp-remove-link-${linkIdx}`}>
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

/**
 * Inline card view for a setlist song — mirrors the ItemDetailView pattern.
 * Renders as a plain in-page card; SetlistTab swaps the list for this view.
 */
const SongDetailPanel = ({
  serviceId,
  song,
  programItems,
  hasEditPermission,
  onClose,
  onUpdateKey,
  onUpdateTempo,
  onUpdateAccidental,
  onAddLink,
  onRemoveLink,
  onAddAttachment,
  onRemoveAttachment,
  onOpenViewer,
  onRemove,
  contents,
  loadingContent,
  onFetchContent,
}) => {
  const [lyricsOnly, setLyricsOnly] = useState(false);
  const [accidental, setAccidental] = useState(() => song.accidental || detectAccidental(song.key));
  const [copied, setCopied] = useState(false);
  const [keyVal, setKeyVal] = useState(song.key || '');
  const [bpmVal, setBpmVal] = useState(song.bpm || '');
  // Capo — personal, device-only preference (never synced to other users)
  const [capo, setCapo] = useState(() => getStoredCapo(song.song_id) ?? 0);
  const [showAddLink, setShowAddLink] = useState(false);

  const rawContent = contents?.[song.song_id] || '';
  const isLoadingContent = !!loadingContent?.[song.song_id];

  // Fetch content on mount
  useEffect(() => {
    if (!contents?.[song.song_id] && !loadingContent?.[song.song_id]) {
      onFetchContent?.(song.song_id);
    }
  }, [song.song_id]); // eslint-disable-line

  // Sync local inputs when song changes
  useEffect(() => {
    setKeyVal(song.key || '');
    setBpmVal(song.bpm || '');
    setAccidental(song.accidental || detectAccidental(song.key));
    setCapo(getStoredCapo(song.song_id) ?? 0);
  }, [song.song_id, song.key, song.bpm, song.accidental]);

  // Once content loads, default capo from the file's {capo:} directive if no preference is stored yet
  useEffect(() => {
    if (getStoredCapo(song.song_id) !== null) return;
    const fc = parseCapoDirective(rawContent);
    if (fc !== null) setCapo(fc);
  }, [rawContent, song.song_id]);


  const targetKey = song.key || null;

  const chartHtml = useMemo(() => {
    if (!rawContent) return '';
    try {
      const META_RE = /^\{(title|subtitle|artist|composer|lyricist|album|key|capo|tempo|time|duration|year|ccli|copyright|flow|sorttitle|sortartist|url|google_doc_key)[^}]*\}\s*\n?/gim;
      const fileKeyMatch = rawContent.match(/^\{key\s*:\s*([^}]+)\}/im);
      const fileKey = fileKeyMatch ? fileKeyMatch[1].trim() : null;
      const cleaned = rawContent.replace(META_RE, '');
      let parsed = _parser.parse(cleaned);
      if (targetKey && fileKey && fileKey !== targetKey) {
        const SEMI = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B'];
        const FLAT = { Db:1, Eb:3, Gb:6, Ab:8, Bb:10, Cb:11, Fb:4 };
        const toIdx = k => {
          const r = k?.match(/^([A-G][b#]?)/)?.[1];
          if (!r) return -1;
          const i = SEMI.indexOf(r);
          return i !== -1 ? i : (FLAT[r] ?? -1);
        };
        const fromIdx = toIdx(fileKey);
        const toIdxVal = toIdx(targetKey);
        if (fromIdx !== -1 && toIdxVal !== -1) {
          const delta = ((toIdxVal - fromIdx) + 12) % 12;
          if (delta !== 0) parsed = parsed.transpose(delta);
        }
      }
      if (capo) parsed = parsed.transpose(-capo);
      return postProcessChordHtml(_formatter.format(parsed), accidental);
    } catch { return ''; }
  }, [rawContent, targetKey, accidental, capo]);

  const handleCopy = useCallback(async () => {
    if (!rawContent) return;
    const text = lyricsOnly ? stripToLyrics(rawContent) : rawContent;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  }, [rawContent, lyricsOnly]);

  // Step key by ±1 semitone and save immediately
  const handleKeyStep = (delta) => {
    const newKey = transposeKey(song.key || '', delta, accidental);
    if (!newKey) return;
    setKeyVal(newKey);
    onUpdateKey?.(newKey);
  };

  const handleKeyBlur = () => { if (keyVal !== song.key) onUpdateKey?.(keyVal); };
  const handleBpmBlur = () => { if (bpmVal !== (song.bpm || '')) onUpdateTempo?.(bpmVal); };

  // Step capo — personal preference, saved to this device only
  const stepCapo = (delta) => {
    setCapo(c => {
      const next = Math.max(CAPO_MIN, Math.min(CAPO_MAX, c + delta));
      setStoredCapo(song.song_id, next);
      return next;
    });
  };



  const handleRemoveAttachment = async (attachmentId) => {
    try {
      await servicesAPI.deleteSetlistAttachment(serviceId, song.song_id, attachmentId);
      onRemoveAttachment?.(attachmentId);
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  const links = song.links || [];
  const attachments = song.attachments || [];
  const resourceCount = links.length + attachments.length;

  return (
    <>
      <style>{CHORD_SHEET_CSS}</style>

      {/* ── Card (mirrors ItemDetailView wrapper) ───────────────── */}
      <div
        className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-white/10 rounded-xl shadow-sm space-y-0 overflow-hidden"
        data-testid="song-detail-panel"
      >
        {/* Card header: song info + actions */}
        <div className="px-5 pt-5 pb-4 border-b border-slate-100 dark:border-white/5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white leading-tight break-words">
                {song.title}
              </h2>
              {song.artist && (
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{song.artist}</p>
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {hasEditPermission && (
                <DeleteButton
                  onConfirm={onRemove}
                  className="p-1.5 rounded-lg shrink-0"
                  title="Remove from setlist"
                  testId="song-detail-remove-btn"
                />
              )}
            </div>
          </div>



        </div>

        {/* ── Song content ──────────────────────────────────────── */}
        <div className="border-b border-slate-100 dark:border-white/5">
          {/* Controls bar */}
          <div className="flex items-center gap-2 px-5 py-3 flex-wrap border-b border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-800/40">
            {/* Key control — always show; editing saves to setlist */}
            {!lyricsOnly && (
              <div className="flex items-center gap-1" data-testid="sdp-key-control">
                <span className="text-[10px] font-semibold text-slate-400 uppercase mr-0.5">Key</span>
                <button onClick={() => handleKeyStep(-1)} data-testid="sdp-key-down"
                  disabled={!hasEditPermission}
                  className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-600 text-sm font-bold transition-colors disabled:opacity-30 disabled:cursor-not-allowed">−</button>
                {hasEditPermission ? (
                  <input
                    type="text"
                    data-testid="song-detail-key-input"
                    value={keyVal}
                    onChange={(e) => setKeyVal(e.target.value)}
                    onBlur={handleKeyBlur}
                    className="w-12 h-7 px-1 text-xs text-center font-mono font-semibold border border-slate-200 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 focus:border-green-400 focus:ring-1 focus:ring-green-200 outline-none text-slate-900 dark:text-white"
                    placeholder="—"
                  />
                ) : (
                  <span className="px-2 py-0.5 rounded-md text-xs font-mono font-semibold bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200 min-w-[3rem] text-center"
                    data-testid="sdp-current-key">
                    {song.key || '—'}
                  </span>
                )}
                <button onClick={() => handleKeyStep(1)} data-testid="sdp-key-up"
                  disabled={!hasEditPermission}
                  className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-600 text-sm font-bold transition-colors disabled:opacity-30 disabled:cursor-not-allowed">+</button>
                <button
                  onClick={() => {
                    const next = accidental === 'b' ? '#' : 'b';
                    setAccidental(next);
                    onUpdateAccidental?.(next);
                  }}
                  data-testid="sdp-accidental-toggle"
                  className="w-6 h-6 flex items-center justify-center rounded text-xs font-bold transition-colors ml-0.5 bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-500">
                  {accidental === 'b' ? '♭' : '♯'}
                </button>
              </div>
            )}
            {/* BPM control */}
            {!lyricsOnly && (
            <div className="flex items-center gap-1">
              <span className="text-[10px] font-semibold text-slate-400 uppercase">BPM</span>
              {hasEditPermission ? (
                <input
                  type="text"
                  data-testid="song-detail-tempo-input"
                  value={bpmVal}
                  onChange={(e) => setBpmVal(e.target.value)}
                  onBlur={handleBpmBlur}
                  className="w-14 h-7 px-1 text-xs text-center font-mono border border-slate-200 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 focus:border-green-400 focus:ring-1 focus:ring-green-200 outline-none text-slate-900 dark:text-white"
                  placeholder={song.originalBpm || '—'}
                />
              ) : (
                <span className="px-2 py-0.5 rounded-md text-xs font-mono bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300 min-w-[3rem] text-center">
                  {song.bpm || '—'}
                </span>
              )}
            </div>
            )}
            {/* Capo control — personal device-only preference; visible to anyone, never shared with the team */}
            {!lyricsOnly && (
            <div className="flex items-center gap-1" data-testid="sdp-capo-control">
              <span className="text-[10px] font-semibold text-slate-400 uppercase">Capo</span>
              <button onClick={() => stepCapo(-1)} disabled={capo <= CAPO_MIN} data-testid="sdp-capo-down"
                className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-600 text-sm font-bold transition-colors disabled:opacity-30 disabled:cursor-not-allowed">−</button>
              <span
                data-testid="sdp-capo-value"
                title={capo > 0 ? `Capo on fret ${capo} — personal preference, not shared with your team` : 'No capo'}
                className={`px-2 py-0.5 rounded-md text-xs font-mono font-semibold min-w-[2rem] text-center ${capo > 0 ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300' : 'bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300'}`}
              >
                {capo}
              </span>
              <button onClick={() => stepCapo(1)} disabled={capo >= CAPO_MAX} data-testid="sdp-capo-up"
                className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-600 text-sm font-bold transition-colors disabled:opacity-30 disabled:cursor-not-allowed">+</button>
            </div>
            )}
            {/* Chords/Lyrics + Copy — joined button group, right-justified */}
            <div className="flex items-center rounded-lg overflow-hidden border border-slate-200 dark:border-slate-600 divide-x divide-slate-200 dark:divide-slate-600 sm:ml-auto">
              <button
                onClick={() => setLyricsOnly(v => !v)}
                data-testid="sdp-lyrics-toggle"
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-all ${
                  lyricsOnly
                    ? 'bg-green-600 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                }`}
              >
                {lyricsOnly ? <AlignLeft className="w-3.5 h-3.5" /> : <Music2 className="w-3.5 h-3.5" />}
                {lyricsOnly ? 'Lyrics' : 'Chords'}
              </button>
              <button
                onClick={handleCopy}
                disabled={!rawContent || isLoadingContent}
                data-testid="song-detail-copy-btn"
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-all ${
                  copied
                    ? 'bg-green-600 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed'
                }`}
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">{isLoadingContent ? 'Loading…' : copied ? 'Copied!' : 'Copy'}</span>
              </button>
            </div>
          </div>

          {/* Song content */}
          <div className="px-5 py-5" data-testid="sdp-song-content">
            {isLoadingContent ? (
              <div className="flex items-center gap-2 py-8 text-slate-400">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500" />
                <span className="text-sm">Loading…</span>
              </div>
            ) : !rawContent ? (
              <p className="text-sm text-slate-400 italic py-6 text-center">No content available for this song.</p>
            ) : (
              <div className={`sdp-csj-wrap${lyricsOnly ? ' sdp-lyrics-mode' : ''}`} dangerouslySetInnerHTML={{ __html: chartHtml }} />
            )}
          </div>
        </div>

        {/* ── Resources ─────────────────────────────────────────── */}
        <div className="px-5 py-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Resources
              {resourceCount > 0 && (
                <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400">{resourceCount}</span>
              )}
            </p>
            {hasEditPermission && (
              <button
                onClick={() => setShowAddLink(true)}
                data-testid="song-detail-add-link-option"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
              >
                <Link2 className="w-3.5 h-3.5" />
                Add Link
              </button>
            )}
          </div>

          {resourceCount === 0 ? (
            <div className="flex flex-col items-center py-5 text-center border border-dashed border-slate-200 dark:border-slate-700 rounded-xl">
              <Paperclip className="w-5 h-5 text-slate-300 mb-1.5" />
              <p className="text-sm text-slate-400">No resources attached yet</p>
              {hasEditPermission && <p className="text-xs text-slate-300 mt-0.5">Add a link or file above</p>}
            </div>
          ) : (
            <div className="space-y-1.5" data-testid="song-detail-resources-list">
              {attachments.map((att) => (
                <div key={att.attachment_id}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50"
                  data-testid={`song-detail-attachment-${att.attachment_id}`}>
                  <Paperclip className="w-4 h-4 text-slate-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{att.original_name}</p>
                    {att.size && <p className="text-xs text-slate-400">{formatFileSize(att.size)}</p>}
                  </div>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}${att.url}`}
                    download={att.original_name}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 text-slate-400 hover:text-green-600 transition-colors shrink-0"
                    data-testid={`sdp-download-${att.attachment_id}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Download className="w-4 h-4" />
                  </a>
                  {hasEditPermission && (
                    <DeleteButton
                      onConfirm={() => handleRemoveAttachment(att.attachment_id)}
                      className="p-1.5 shrink-0"
                      title="Remove attachment"
                      testId={`sdp-remove-attachment-${att.attachment_id}`}
                    />
                  )}
                </div>
              ))}
              {links.map((link, linkIdx) => (
                <RichLink
                  key={linkIdx}
                  link={link}
                  linkIdx={linkIdx}
                  hasEditPermission={hasEditPermission}
                  onRemove={onRemoveLink}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add link modal */}
      {showAddLink && (
        <AddLinkModal
          songTitle={song.title}
          onAdd={(link) => { onAddLink(link); setShowAddLink(false); }}
          onClose={() => setShowAddLink(false)}
        />
      )}
    </>
  );
};

export default SongDetailPanel;
